import React, { useState, useEffect, useRef, useCallback } from 'react';

import { Layout, Button, Table, Modal, Row, Col, Input, Card, Space, Tag, message, Tabs, Tooltip } from 'antd';
import { SearchOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

import useAuth from 'hooks/useAuth';
import { userList, userDelete, userUpgradePremium, userDowngradeFree } from 'apis/user';

import UserUpdateForm from './forms/user-update.form';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

import './styles.less';

import { useUserHistory } from 'hooks/use-user-history';
import UserPaymentChart from './components/user-payment-chart';
import UserUsageChart from './components/user-usage-chart';
import { usePaymentChartData, useUsageChartData } from 'hooks/use-user-chart-data';
import UserStatsCard from 'components/ui/user-stats-card';
import SearchToolbar from 'components/ui/search-toolbar';

const errorMessages = [
  '',
  'NO_DEVICE_FOUND_TOOLTIP',
  'CORRUPTED_INSTALLATION',
  '',
  'MIC_ACCESS_DENIED',
];

const Users = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [userUpdate, setUserUpdate] = useState({});

  const [activeTab, setActiveTab] = useState('email');

  const [fromDateFilter, setFromDateFilter] = useState(moment().subtract(3, 'months'));
  const [toDateFilter, setToDateFilter] = useState(moment());

  const [isUpdateModal, setIsUpdateModal] = useState(false);

  const { setDaysData: setPaymentDaysData, stats: paymentStats, setStats: setPaymentStats, onReset: onPaymentReset } = usePaymentChartData();
  const { setDaysData: setUsageDaysData, stats: usageStats, setStats: setUsageStats, onReset: onUsageReset } = useUsageChartData();
  const {
    userIdHistory, addUserIdHistory, pageHistory, incrementPageHistory, limitHistory, totalPageHistory, setTotalPageHistory,
    isPaused, setIsPaused, initialLoading, setInitialLoading, isCompleted, setIsCompleted, onReset, setTotal,
    usersHistoryFilter, addUsersHistoryRef, updateUserById, removeUserById,
    page, pageSize, applyFiltersNow, filteredUsersRef, fromDate, setFromDate, toDate, setToDate, email, setEmail
  } = useUserHistory();

  const onChangeActiveTab = (newValue) => {
    setActiveTab(newValue);
  };

  const loadingRef = useRef(false);
  const workerRef = useRef(null);

  useEffect(() => {
    workerRef.current = new Worker(
      new URL('../../../workers/chart.worker.js', import.meta.url),
      { type: 'module' }
    );

    workerRef.current.onmessage = (e) => {
      if (e.data.type === 'UPDATED_DAYS_DATA') {
        const { paymentDaysData, paymentStats, usageDaysData, usageStats } = e.data.payload;
        setPaymentDaysData(paymentDaysData);
        setPaymentStats(paymentStats);
        setUsageDaysData(usageDaysData);
        setUsageStats(usageStats);
      }
    };

    return () => workerRef.current.terminate();
  }, [setPaymentDaysData, setPaymentStats, setUsageDaysData, setUsageStats]);

  const searchInputEl = useRef(null);

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInputEl}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => {
            confirm();
            const { setFilter } = useUserHistory.getState();
            setFilter({ [dataIndex]: (selectedKeys[0] || "").toLowerCase() });
          }}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
            onClick={() => {
              confirm();
              const { setFilter } = useUserHistory.getState();
              setFilter({ [dataIndex]: (selectedKeys[0] || "").toLowerCase() });
            }}
          >
            Search
          </Button>
          <Button
            size="small"
            style={{ width: 90 }}
            onClick={() => {
              clearFilters();
              confirm();
              useUserHistory.getState().resetFilterKey(dataIndex);
            }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilterDropdownVisibleChange: visible => {
      if (visible) setTimeout(() => searchInputEl.current?.select(), 100);
    },
    render: (text, record) => {
      const v = record?.[dataIndex];
      return <div className="truncate max-w-[240px]">{v ?? ""}</div>;
    },
  });

  const columns = [
    {
      title: 'Country',
      align: 'center',
      width: '5%',
      ...getColumnSearchProps('countryCode'),
    },
    {
      title: 'Created At',
      align: 'center',
      dataIndex: 'createdAt',
      width: '10%',
      sorter: true,
      defaultSortOrder: 'descend',
      render: (_, r) => <div>{moment(r.createdAt).format('YYYY-MM-DD HH:mm')}</div>,
    },
    {
      title: 'Email',
      width: '18%',
      ...getColumnSearchProps('email'),
    },
    {
      title: 'Role',
      align: 'center',
      dataIndex: 'role',
      width: '8%',
      filters: [
        { text: 'Free', value: 'free' },
        { text: 'Pre', value: 'pre' },
      ],
      render: (v) => v === 'free' ? <Tag>FREE</Tag> : <Tag color="green">PRE</Tag>,
    },
    {
      title: 'Source',
      align: 'center',
      dataIndex: 'source',
      width: '8%',
      filters: [
        { text: 'Appsumo', value: 'appsumo' },
        { text: 'Gumroad', value: 'gumroad' },
        { text: 'Enterprise', value: 'enterprise' },
        { text: 'Giveaway', value: 'giveaway' },
        { text: 'Manual invoice', value: 'manual-invoice' },
      ],
      render: (v) => {
        return (
          <div>
            {v ? <Tag color="green">{String(v).toUpperCase()}</Tag> : null}
            {v?.gumroad?.quantity && (
              <div style={{ fontSize: 10, marginTop: 5, marginLeft: -5 }}>{v?.gumroad?.quantity} SEAT</div>
            )}
            {v?.quantity && (
              <div style={{ fontSize: 10, marginTop: 5, marginLeft: -5 }}>{v?.quantity} SEAT</div>
            )}
            {v?.source === "manual-invoice" && v?.seat && (
              <div style={{ fontSize: 10, marginTop: 5, marginLeft: -5 }}>{v?.seat} SEAT</div>
            )}
            {v?.payments > 0 && (
              <a target='_blank' rel="noopener noreferrer" href={`/v1/users/sales?email=${v?.email}`} style={{ fontSize: 10, marginTop: 5, marginLeft: -5 }}>{v?.payments} Payment</a>
            )}
          </div>
        );
      },
    },
    {
      title: 'EndTime',
      align: 'center',
      dataIndex: 'endTime',
      width: '10%',
      sorter: true,
      defaultSortOrder: 'descend',
      render: (_, r) => <div>{moment(r.endTime).format('YYYY-MM-DD HH:mm')}</div>,
    },
    {
      title: 'Installed',
      dataIndex: 'isInstalledSuccessfully',
      render: (_, r) => {
        if (r?.isInstalledSuccessfully === true) {
          return <Tag color="green">Success</Tag>;
        } else if (r?.isInstalledSuccessfully === false) {
          return <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
            <Tag color="red">Error</Tag>
            <div style={{ textAlign: 'left' }}>Mic: <span>{errorMessages[parseInt(r?.micError)]}</span></div>
            <div style={{ textAlign: 'left', marginTop: 5 }}>Speaker: <span>{errorMessages[parseInt(r?.speakerError)]}</span></div>
            <div style={{ textAlign: 'left', marginTop: 5 }}>
              <Tooltip title={r?.appSettings}>
                <span>AppSettings</span>
              </Tooltip>
            </div>
          </div>;
        }
      },
      width: '10%',
      align: 'center',
      filterMultiple: true,
      filters: [
        { text: 'Success', value: true },
        { text: 'Error', value: false },
        { text: 'Undefined', value: null },
      ],
      onFilter: (value, record) => {
        if (value === true) {
          return record.isInstalledSuccessfully === true;
        } else if (value === false) {
          return record.isInstalledSuccessfully === false;
        } else {
          return record.isInstalledSuccessfully === undefined;
        }
      },
    },
    {
      title: 'Usages',
      align: 'center',
      dataIndex: 'isUsages',
      width: '8%',
      filters: [
        { text: 'Used', value: true },
        { text: 'Unused', value: false },
      ],
      render: (v) => v === true ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>,
    },
    {
      title: 'Expired',
      align: 'center',
      dataIndex: 'isAfter',
      width: '8%',
      filters: [{ text: 'Expired', value: false }],
      render: (v) => v === false ? <Tag color="red">Expired</Tag> : null,
    },
    {
      title: 'Recorded',
      align: 'center',
      dataIndex: 'recordingSize',
      width: '4%',
      render: (record) => {
        return (
          <div>
            {record > 0 ? record : ""}
          </div>
        );
      },
      filters: [
        { text: 'Recorded', value: true },
        { text: 'Unrecorded', value: false }
      ],
      onFilter: (value, record) => {
        if (value) {
          return record.recordingSize > 0;
        } else {
          return record.recordingSize <= 0;
        }
      },
    },
    {
      width: '8%',
      key: "actionMembership",
      render: (record) =>
        <div>
          {(user?.role === "super") && (
            <>
              <Button
                type='link'
                disabled={record?.role === 'pre'}
                ghost
                onClick={() => {
                  Modal.confirm({
                    title: 'Confirm',
                    icon: <ExclamationCircleOutlined />,
                    content: 'Are you sure to upgrade premium (source = Giveaway)',
                    okText: 'Ok',
                    cancelText: 'No',
                    onOk: async () => {
                      try {
                        setInitialLoading(true);
                        await userUpgradePremium({ userId: record?.userId });
                        updateUserById(record?.userId, {
                          role: 'pre',
                          source: 'giveaway',
                          endTime: moment().clone().add(1, 'month')
                        });
                        message.success('Upgrade premium is successful');
                      } catch (err) {
                        console.error(err);
                        message.error('Upgrade failed');
                      } finally {
                        setInitialLoading(false);
                      }
                    }
                  });
                }}
              >
                Upgrade
              </Button>
              <Button
                type='link'
                disabled={record?.role === 'free'}
                ghost
                onClick={() => {
                  Modal.confirm({
                    title: 'Confirm',
                    icon: <ExclamationCircleOutlined />,
                    content: 'Are you sure to downgrade free?',
                    okText: 'Ok',
                    cancelText: 'No',
                    onOk: async () => {
                      try {
                        setInitialLoading(true);
                        await userDowngradeFree({ userId: record?.userId });
                        updateUserById(record?.userId, { role: 'free' });
                        message.success('Downgrade free is successful');
                      } catch (err) {
                        console.error(err);
                        message.error('Downgrade failed');
                      } finally {
                        setInitialLoading(false);
                      }
                    }
                  });
                }}
              >
                Downgrade
              </Button>
            </>
          )
          }
        </div>
    },
    {
      width: '8%',
      key: "actionManage",
      render: (record) =>
        <div>
          {(user?.role === "super") && (
            <div>
              <Button
                type='link'
                onClick={() => {
                  navigate(`/v1/users/usage-detail/${record.userId}`)
                }}
              >
                View Usage
              </Button>
              <Button
                type='link'
                disabled={record?.role === 'free'}
                onClick={() => {
                  const user = usersHistoryFilter.find(
                    (item) => item.userId === record.userId
                  );
                  setUserUpdate(user);
                  setIsUpdateModal(true);
                }}
              >
                Edit
              </Button>
              <Button
                style={{ color: 'red' }}
                type='link'
                ghost
                onClick={() => {
                  Modal.confirm({
                    title: 'Confirm',
                    content: 'Are you sure to delete this user?',
                    okText: 'Ok',
                    cancelText: 'No',
                    onOk: async () => {
                      try {
                        setInitialLoading(true);
                        await userDelete({ userId: record?.userId, email: record?.email });
                        removeUserById(record.userId);
                        message.success('This user has been deleted successfully');
                      } catch (err) {
                        console.error(err);
                        message.error('Failed to delete user');
                      } finally {
                        setInitialLoading(false);
                      }
                    }
                  });
                }}
              >
                Delete
              </Button>
            </div>
          )}
        </div>
    },
  ];

  useEffect(() => {
    const { resetFilterAndSorter, paginate, fromDate, toDate } = useUserHistory.getState();

    setFromDateFilter(fromDate);
    setToDateFilter(toDate);
    resetFilterAndSorter();
    paginate(1, 20);
  }, []);

  const handleTableChange = (pagination, filters, sorter) => {
    const { setFilterAndSorter, paginate } = useUserHistory.getState();

    setFilterAndSorter({
      filter: {
        role: Array.isArray(filters.role) ? filters.role : [],
        source: Array.isArray(filters.source) ? filters.source : [],
        isUsages: filters.isUsages?.length ? (String(filters.isUsages[0]) === 'true') : null,
        isInstalledSuccessfully: filters.isInstalledSuccessfully?.length ? (String(filters.isInstalledSuccessfully[0]) === 'true') : null,
        isAfter: filters.isAfter?.length ? (String(filters.isAfter[0]) === 'true') : null,
        recordingSize: filters.recordingSize?.length
          ? (String(filters.recordingSize[0]) === 'true')
          : null,
      },
      sorter: sorter?.order
        ? { field: sorter.field, order: sorter.order }
        : { field: "createdAt", order: "descend" }
    })

    paginate(pagination.current, pagination.pageSize);
  };

  function onChange(dates, dateStrings) {
    if (!dates) {
      setFromDate(undefined);
      setToDate(undefined);
      setFromDateFilter(undefined);
      setToDateFilter(undefined);
    }

    if (dates && dates[0]) {
      setFromDate(dates[0])
      setFromDateFilter(dates[0])
    }

    if (dates && dates[1]) {
      setToDate(dates[1])
      setToDateFilter(dates[1])
    }
  }

  const fetchUsers = useCallback(async (currentPage = 1, currentSize = 1000, lastUserId = null) => {
    let userLength = 0;
    let totalPage = 0;

    try {
      const payload = {
        page: currentPage,
        limit: currentSize ? currentSize : limitHistory,
        lastVisibleId: lastUserId
      };

      payload.fromDate = fromDateFilter.format('YYYY-MM-DD');
      payload.toDate = toDateFilter.format('YYYY-MM-DD');

      if (activeTab === "email") {
        if (!email) {
          setIsPaused(false);
          setIsCompleted(true);
          return;
        }
        payload.email = email;
      }

      const res = await userList(payload);

      addUsersHistoryRef(res.result.users);
      setTotalPageHistory(res.result.totalPage);
      setTotal(res.result.total);

      if (currentPage === 1) {
        applyFiltersNow();
      }

      if (res.result.users.length > 0) {
        const paymentDaysData = usePaymentChartData.getState().daysData;
        const paymentStats = usePaymentChartData.getState().stats;
        const usageDaysData = useUsageChartData.getState().daysData;
        const usageStats = useUsageChartData.getState().stats;
        const allChartUsers = res.result.users.map(u => ({
          createdAt: u.createdAt,
          role: u.role,
          payments: u.payments,
          isUsages: u.isUsages
        }));

        workerRef.current.postMessage({
          type: 'PROCESS_USERS_STATS',
          payload: {
            currentPaymentDays: paymentDaysData,
            currentPaymentStats: paymentStats,
            currentUsageDays: usageDaysData,
            currentUsageStats: usageStats,
            newUsers: allChartUsers,
            fromDate: fromDateFilter.toISOString(),
            toDate: toDateFilter.toISOString()
          }
        });
      }

      if (res.result.users.length > 0) {
        const lastUser = res.result.users[res.result.users.length - 1];

        if (lastUser) {
          addUserIdHistory(lastUser.userId);
        }

        if (currentPage === res.result.totalPage) {
          setIsPaused(false);
          setIsCompleted(true);
        }
      }

      userLength = res.result.users.length;
      totalPage = res.result.totalPage;
    } catch (error) {
      console.log("error when fetching page:", error);
      message.error("Failed to fetch users");
    } finally {
      return {
        userLength,
        totalPage
      };
    }
  }, [limitHistory, fromDateFilter, toDateFilter, activeTab, addUsersHistoryRef, setTotalPageHistory, setTotal, email, applyFiltersNow, addUserIdHistory, setIsPaused, setIsCompleted]);

  const loadPage = useCallback(async () => {
    try {
      loadingRef.current = true;

      const lastUserId = pageHistory > 1 ? userIdHistory[pageHistory - 2] : null;  // Lấy lastUserId từ history
      const usersFetched = await fetchUsers(pageHistory, limitHistory, lastUserId);

      // Nếu có thêm dữ liệu, tiếp tục tăng pageHistory
      if (usersFetched.userLength > 0) {
        if (pageHistory === 1) {
          if (usersFetched.totalPage > 1) {
            incrementPageHistory();
          }
        } else {
          if (pageHistory < totalPageHistory) {
            incrementPageHistory();
          }
        }
      }

      if (pageHistory === 1) {
        setInitialLoading(false); // Tắt loading lần đầu sau khi trang đầu tiên tải xong
      }
    } catch (error) {
      console.log("error in loadPage:", error);
    } finally {
      loadingRef.current = false;
    }
  }, [fetchUsers, incrementPageHistory, limitHistory, pageHistory, setInitialLoading, totalPageHistory, userIdHistory]);

  const onResetState = async () => {
    onReset();
    onPaymentReset();
    onUsageReset();
  };

  useEffect(() => {
    if (user && pageHistory <= totalPageHistory && !isPaused && !loadingRef.current && !isCompleted) {
      loadPage();
    }
  }, [user, pageHistory, totalPageHistory, isPaused, isCompleted, loadPage]);

  useEffect(() => {
    if (activeTab === "date") {
      setFromDateFilter(fromDate);
      setToDateFilter(toDate);
    } else if (activeTab === "email") {
      setFromDateFilter(moment("2020-01-01", "YYYY-MM-DD"));
      setToDateFilter(moment());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Hàm xử lý button dừng/tiếp tục
  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  return (
    <Layout className="jobs-page">
      <Layout style={{ paddingLeft: 24, paddingRight: 24, marginTop: 20 }} >
        <Card style={{ marginBottom: 18 }}>
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }} justify="space-between">
            <Col className="gutter-row" span={12}>
              <h2>Users</h2>
            </Col>
          </Row>
          <Row>
            <Tabs
              activeKey={activeTab}
              onChange={onChangeActiveTab}
              className='w-full'
            >
              <Tabs.TabPane
                tab="Search by Email"
                key="email"
                disabled={!(user && (pageHistory <= totalPageHistory || totalPageHistory === 0) && !isPaused && !loadingRef.current)}
              >
                <SearchToolbar
                  type="email"
                  email={email}
                  onChange={setEmail}
                  onSearch={onResetState}
                  disabled={!(user && (pageHistory <= totalPageHistory || totalPageHistory === 0) && !isPaused && !loadingRef.current)}
                  togglePause={togglePause}
                />
              </Tabs.TabPane>
              <Tabs.TabPane
                tab="Search by Date"
                key="date"
                disabled={initialLoading || !(user && (pageHistory <= totalPageHistory || totalPageHistory === 0) && !isPaused && !loadingRef.current)}
              >
                <SearchToolbar
                  type="date"
                  fromDate={fromDate}
                  toDate={toDate}
                  onChange={onChange}
                  onSearch={onResetState}
                  disabled={initialLoading || !(user && (pageHistory <= totalPageHistory || totalPageHistory === 0) && !isPaused && !loadingRef.current)}
                  togglePause={togglePause}
                />
              </Tabs.TabPane>
            </Tabs>
          </Row>
        </Card>

        <UserStatsCard
          titleMap={{
            total: "Total",
            col1: "Paid",
            col2: "Unpaid",
            percentage: "Percentage of paid users / total users"
          }}
          stats={{
            total: paymentStats.total,
            col1: paymentStats.paid,
            col2: paymentStats.unpaid
          }}
          initialLoading={initialLoading}
          chartComponent={
            <UserPaymentChart />
          }
        />

        <UserStatsCard
          titleMap={{
            total: "Total",
            col1: "Used",
            col2: "Unused",
            percentage: "Percentage of used users / total users"
          }}
          stats={{
            total: usageStats.total,
            col1: usageStats.used,
            col2: usageStats.unused
          }}
          initialLoading={initialLoading}
          chartComponent={
            <UserUsageChart />
          }
        />

        <Table
          loading={initialLoading}
          bordered
          columns={columns.filter(c => c.key === "action"
            ? (user?.role === "super" || user?.role === "sale")
            : true)}
          dataSource={usersHistoryFilter}
          rowKey={(row) => row.userId}
          pagination={{
            current: page,
            pageSize,
            total: Number(filteredUsersRef.length),
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
        />

        <Modal
          open={isUpdateModal}
          title={`User / Edit`}
          onCancel={() => setIsUpdateModal(false)}
          destroyOnClose={true}
          width={980}
          centered
          footer={null}
        >
          <UserUpdateForm
            user={userUpdate}
            onCancel={() => setIsUpdateModal(false)}
            onCallback={async (endTime, source, seat) => {
              updateUserById(userUpdate.userId, {
                endTime: moment(endTime),
                source: source,
                isAfter: moment(endTime).isAfter(new moment()),
                seat
              });
              setInitialLoading(false);
              setIsUpdateModal(false);
            }}
          />
        </Modal>
      </Layout>
    </Layout>
  );
};

export default Users;
